import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, RefreshCw, Languages, Mail, Bell, MessageCircle, Megaphone } from "lucide-react";

const AUDIENCES = [
  { key: "all", label: "Все салоны" },
  { key: "whatsapp", label: "С подключённым WhatsApp" },
  { key: "whatsapp_down", label: "WhatsApp НЕ авторизован (отвалился)" },
];
const CHANNELS = [
  { key: "email", label: "Email", Icon: Mail }, { key: "push", label: "Push владельцу", Icon: Bell },
  { key: "telegram", label: "Telegram (если подключён)", Icon: MessageCircle }, { key: "banner", label: "Баннер в кабинете", Icon: Megaphone },
];

export default function AdminBroadcasts() {
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
  const [templates, setTemplates] = useState({});
  const [form, setForm] = useState({ subject: "", text: "", link: "", link_label: "", audience: "all", translate: true,
    channels: { email: true, push: true, telegram: true, banner: true } });
  const [audience, setAudience] = useState(null);
  const [waCheck, setWaCheck] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadHistory = () => axios.get(`${API}/admin/broadcasts`, auth).then((r) => setHistory(r.data.broadcasts || [])).catch(() => {});
  useEffect(() => {
    axios.get(`${API}/admin/broadcasts/templates`, auth).then((r) => setTemplates(r.data.templates || {})).catch(() => {});
    loadHistory();
  }, []);
  useEffect(() => {
    setAudience(null);
    axios.get(`${API}/admin/broadcasts/audience`, { ...auth, params: { audience: form.audience } }).then((r) => setAudience(r.data)).catch(() => {});
  }, [form.audience, waCheck]);

  const applyTemplate = (k) => { const t = templates[k]; if (t) setForm((f) => ({ ...f, subject: t.subject, text: t.text, link: t.link, link_label: t.link_label, audience: t.audience || f.audience })); setPreview(null); };
  const checkWa = async () => {
    setBusy("wa");
    try { const r = await axios.post(`${API}/admin/broadcasts/check-whatsapp`, {}, auth); setWaCheck(r.data); toast.success(`Проверено ${r.data.checked}: авторизовано ${r.data.authorized}, отвалилось ${r.data.down}`); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(""); }
  };
  const doPreview = async () => {
    setBusy("preview");
    try { const r = await axios.post(`${API}/admin/broadcasts/preview`, { subject: form.subject, text: form.text, link_label: form.link_label }, auth); setPreview(r.data.translations); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(""); }
  };
  const send = async (test) => {
    if (!test && !window.confirm(`Отправить ${audience?.count || 0} владельцам? Это действие нельзя отменить.`)) return;
    setBusy(test ? "test" : "send");
    try {
      const r = await axios.post(`${API}/admin/broadcasts/send`, { ...form, test_email: test ? testEmail : null }, auth);
      toast.success(test ? `Тест отправлен на ${testEmail}` : `Рассылка запущена: ${r.data.recipients} получателей`);
      if (!test) loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(""); }
  };
  const canSend = form.subject.trim() && form.text.trim() && Object.values(form.channels).some(Boolean);
  const inp = "w-full border-2 border-black rounded-lg px-3 py-2 text-sm bg-white text-black";

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-6 text-black" data-testid="admin-broadcasts">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm font-semibold">Шаблон:</span>
          {Object.entries(templates).map(([k, t]) => (
            <Button key={k} variant="outline" size="sm" className="border-2 border-black" onClick={() => applyTemplate(k)} data-testid={`bc-template-${k}`}>{t.subject.slice(0, 40)}</Button>
          ))}
        </div>
        <input className={inp} placeholder="Тема / заголовок" value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="bc-subject" />
        <textarea className={`${inp} min-h-[220px]`} placeholder={"Текст. Плейсхолдеры: {salon_name}, {owner_name}, {link}"} value={form.text} onChange={(e) => set("text", e.target.value)} data-testid="bc-text" />
        <div className="grid sm:grid-cols-2 gap-2">
          <input className={inp} placeholder="Ссылка (кнопка)" value={form.link} onChange={(e) => set("link", e.target.value)} data-testid="bc-link" />
          <input className={inp} placeholder="Текст кнопки" value={form.link_label} onChange={(e) => set("link_label", e.target.value)} data-testid="bc-link-label" />
        </div>
        <div className="flex flex-wrap gap-4 items-center">
          <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
            <input type="checkbox" checked={form.translate} onChange={(e) => set("translate", e.target.checked)} data-testid="bc-translate" />
            <Languages size={16} /> Автоперевод на язык владельца (ru/en/fr/tr)
          </label>
          <Button variant="outline" size="sm" className="border-2 border-black" onClick={doPreview} disabled={!canSend || busy === "preview"} data-testid="bc-preview-btn">
            {busy === "preview" ? "Переводим…" : "Предпросмотр переводов"}
          </Button>
        </div>
        {preview && (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="bc-preview">
            {Object.entries(preview).map(([l, p]) => (
              <div key={l} className="border-2 border-gray-300 rounded-lg p-3 text-xs whitespace-pre-wrap bg-gray-50">
                <div className="font-bold uppercase text-gray-500 mb-1">{l}</div>
                <div className="font-semibold mb-1">{p.subject}</div>{p.text}
                {p.link_label && <div className="mt-2 inline-block bg-black text-white px-2 py-1 rounded">{p.link_label}</div>}
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          {CHANNELS.map(({ key, label, Icon }) => (
            <label key={key} className="flex items-center gap-2 text-sm cursor-pointer border-2 border-black rounded-lg px-3 py-2">
              <input type="checkbox" checked={!!form.channels[key]} onChange={(e) => set("channels", { ...form.channels, [key]: e.target.checked })} data-testid={`bc-channel-${key}`} />
              <Icon size={15} /> {label}
            </label>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center border-t-2 border-black pt-4">
          <input className={`${inp} max-w-xs`} placeholder="email для теста" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="bc-test-email" />
          <Button variant="outline" className="border-2 border-black" onClick={() => send(true)} disabled={!canSend || !testEmail || busy === "test"} data-testid="bc-test-btn">
            {busy === "test" ? "…" : "Тест мне"}
          </Button>
          <Button className="bg-black text-white font-bold ml-auto" onClick={() => send(false)} disabled={!canSend || !audience?.count || busy === "send"} data-testid="bc-send-btn">
            <Send size={16} className="mr-2" />{busy === "send" ? "Отправляем…" : `Отправить ${audience?.count ?? "…"} владельцам`}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="border-2 border-black rounded-lg p-4">
          <div className="font-bold mb-2">Кому</div>
          <div className="space-y-1.5">
            {AUDIENCES.map((a) => (
              <label key={a.key} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="aud" checked={form.audience === a.key} onChange={() => set("audience", a.key)} data-testid={`bc-audience-${a.key}`} /> {a.label}
              </label>
            ))}
          </div>
          <div className="mt-3 text-sm" data-testid="bc-audience-count">
            {audience ? <>Получателей: <b>{audience.count}</b> · Telegram: {audience.with_telegram} · {Object.entries(audience.langs).filter(([, n]) => n).map(([l, n]) => `${l} ${n}`).join(", ")}</> : "…"}
          </div>
          <Button variant="outline" size="sm" className="border-2 border-black mt-3 w-full" onClick={checkWa} disabled={busy === "wa"} data-testid="bc-check-wa-btn">
            <RefreshCw size={14} className={`mr-2 ${busy === "wa" ? "animate-spin" : ""}`} /> Проверить все инстансы Green API
          </Button>
          {waCheck && (
            <div className="mt-3 text-xs" data-testid="bc-wa-result">
              <div>Проверено {waCheck.checked}: ✅ {waCheck.authorized} · ❌ {waCheck.down} · недоступно {waCheck.unreachable}</div>
              <ul className="mt-1 max-h-48 overflow-y-auto space-y-0.5">
                {waCheck.down_list.map((d) => <li key={d.salon_id}>❌ {d.salon_name || d.salon_id} — {d.state}{d.phone ? ` · ${d.phone}` : ""}</li>)}
              </ul>
            </div>
          )}
          {audience?.recipients?.length > 0 && (
            <details className="mt-3 text-xs"><summary className="cursor-pointer font-semibold">Список получателей</summary>
              <ul className="mt-1 max-h-56 overflow-y-auto space-y-0.5">
                {audience.recipients.map((r) => <li key={r.salon_id}>{r.salon_name || "—"} · {r.email} · {r.lang}{r.wa_state ? ` · WA: ${r.wa_state}` : ""}</li>)}
              </ul>
            </details>
          )}
        </div>
        <div className="border-2 border-black rounded-lg p-4">
          <div className="font-bold mb-2">История</div>
          <div className="space-y-2 max-h-80 overflow-y-auto text-xs" data-testid="bc-history">
            {history.length === 0 && <div className="text-gray-500">Пока пусто</div>}
            {history.map((b) => (
              <div key={b.id} className="border border-gray-300 rounded p-2">
                <div className="font-semibold">{b.subject}</div>
                <div className="text-gray-600">{(b.created_at || "").slice(0, 16).replace("T", " ")} · {b.audience} · {b.recipients} получ. · {b.status}</div>
                {b.stats && <div>📧 {b.stats.email || 0} · 🔔 {b.stats.push || 0} · ✈️ {b.stats.telegram || 0} · 📣 {b.stats.banner || 0}{b.stats.errors ? ` · ошибок ${b.stats.errors}` : ""}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
